import Parser from 'html-react-parser';
import * as Immutable from 'immutable';
import * as React from 'react';

import { Link } from 'react-router-dom';

import { localeData } from '../../../locales/data';
import { ResultsProps } from '../../../reducers/LearnReducer/SearchReducer/SearchReducer.types';
import { addModifiers, startWithWhitespace } from '../../../shared/helpers';
import { Pagination } from '../../Pagination/Pagination';
import { PaginationResults } from '../../Pagination/PaginationResults';

import './SearchResults.scss';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { LEARN_PART_PATH_OPERATORS } from '../../../shared/constants';

interface SearchResultsMainProps {
  data: Immutable.List<ResultsProps>;
  title: string;
  newTab?: boolean;
  modifiers?: string[];
  id: string;
  queryTerm: string;
  count: number;
  changePrevPage: () => void;
  changeNextPage: () => void;
  getPaginationNumbers: () => any[];
  changePage: (val: number) => void;
  pageSize: number;
  // The pathname where the current Learn Component it is been displayed
  // Eg : in this route /simulate the value is simulate, for the route /learn the value is learn
  path: string;
  page: number;
  pages: number;
  showPagination: boolean;
}

export const getSearchSlug = (id: string, item: any, path: string) => {
  const itemID = item.get('id');
  switch (id) {
    case 'recommendedReading':
      return item.get('url');
    case 'reference':
      return `/${path}/${LEARN_PART_PATH_OPERATORS}/${itemID}`;
    case 'tutorialPage':
      return `/${path}/documentation/${item.get('tutorial')}/${itemID}`;
    default:
      return '#';
  }
};

export const SearchResultsMain = ({
  data,
  title,
  modifiers = [],
  id,
  newTab,
  queryTerm,
  count,
  changePrevPage,
  changeNextPage,
  getPaginationNumbers,
  changePage,
  pageSize,
  path,
  page,
  pages,
  showPagination,
}: SearchResultsMainProps) => {
  const ResultsPagination = Pagination(PaginationResults);

  return (
    <div className="search-results">
      <h2 className={addModifiers('search-results__main-title', modifiers)}>
        {title} [{data ? data.size : '0'}]
      </h2>
      {data && data.size !== 0 ? (
        <div className="search-results__content">
          <ul className="search-results__list">
            {data.map((item: any, index: number) => (
              <li className="search-results__item" key={`${item.get('id')}-${index}`}>
                {(item.get('title') || item.get('question')) && item.get('id') ? (
                  <h3 className="search-results__title">
                    {newTab === true ? (
                      <Link to={getSearchSlug(id, item, path)} target="blank">
                        {item.get('title')}
                      </Link>
                    ) : (
                      <Link to={getSearchSlug(id, item, path)}>{item.get('title')}</Link>
                    )}
                  </h3>
                ) : null}
                <div className="search-results__txt">
                  {item.get('highlights') && item.get('highlights').size !== 0
                    ? item.get('highlights').map((highlight: any) => {
                        if (highlight.get('content') && highlight.get('content').size !== 0) {
                          return highlight
                            .get('content')
                            .map((content: any, key: number) => (
                              <div key={`search-results-content-${item.get('id')}-${key}`}>
                                {startWithWhitespace(content) ? Parser(content.replace(/^\s/, ' ...')) : Parser(content)}
                              </div>
                            ));
                        } else {
                          return null;
                        }
                      })
                    : null}
                </div>
                {item.get('category') ? (
                  <div className="search-results__cat">
                    <FormattedMessageHelper
                      tagName="span"
                      id="learn.search.article.from"
                      defaultMessage={localeData.en['learn.search.article.from']}
                      values={{ cat: item.get('category') }}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="search-results__pagination">
            <ResultsPagination
              pageSize={pageSize}
              numOfResults={count}
              changePrevPage={changePrevPage}
              changeNextPage={changeNextPage}
              getPaginationNumbers={getPaginationNumbers}
              page={page}
              changePage={changePage}
              pages={pages}
              extraInfo={{ msg: 'pagination.show.results.related', values: { value: title.toLocaleLowerCase() } }}
              showPagination={showPagination}
            />
          </div>
        </div>
      ) : (
        <div className="search-results__no-results">
          <FormattedMessageHelper
            tagName="span"
            id="learn.search.no.results"
            defaultMessage={localeData.en['learn.search.no.results']}
            values={{ term: queryTerm }}
          />
        </div>
      )}
    </div>
  );
};
